import { PERSONA_LABELS } from '@idpe/schema';
import { createFileRoute } from '@tanstack/react-router';

import { Donut } from '~/components/analytics/Donut.js';
import { FiltrosBar } from '~/components/analytics/FiltrosBar.js';
import { KpiCard } from '~/components/analytics/KpiCard.js';
import { LineChart } from '~/components/analytics/LineChart.js';
import { useConversoes } from '~/features/analytics/hooks.js';
import { analyticsSearchSchema, searchToPeriod } from '~/features/analytics/useFiltros.js';
import { downloadCsv, n } from '~/lib/csv.js';

export const Route = createFileRoute('/_auth/analytics/conversoes')({
  validateSearch: (search) => analyticsSearchSchema.parse(search),
  component: AnalyticsConversoes,
});

const TIPO_LABELS: Record<string, string> = {
  contato: 'Contato',
  newsletter: 'Newsletter',
  integridade: 'Integridade',
  sugestao_pauta: 'Sugestão de pauta',
};

function AnalyticsConversoes() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const period = searchToPeriod(search);
  const setRange = (range: '7d' | '30d' | '90d') =>
    void navigate({ search: { ...search, range, from: undefined, to: undefined } });
  const toggleComparar = () => void navigate({ search: { ...search, comparar: !period.comparar } });
  const conv = useConversoes(period);

  const leads = conv.data?.leads;
  const totalLeads = leads
    ? (leads.contato ?? 0) +
      (leads.newsletter ?? 0) +
      (leads.integridade ?? 0) +
      (leads.sugestao_pauta ?? 0)
    : 0;

  // Mapeia buckets para o shape esperado pelo LineChart (visitors=total).
  const buckets = (conv.data?.timeseries ?? []).map((b) => ({
    date: b.date,
    visitors: b.total,
    pageviews: b.total,
  }));

  const personaData = (conv.data?.porPersona ?? []).map((p) => ({
    name: (PERSONA_LABELS as Record<string, string>)[p.persona] ?? p.persona,
    value: p.count,
  }));

  function handleExport() {
    if (!conv.data) return;
    downloadCsv(`analytics-conversoes-${period.range}`, conv.data.timeseries, [
      { key: 'date', label: 'Data' },
      { key: 'total', label: 'Total de leads' },
    ]);
  }

  return (
    <div className="analytics-page">
      <FiltrosBar
        period={period}
        onRangeChange={setRange}
        onToggleComparar={toggleComparar}
        onExportCsv={handleExport}
      />

      <section className="analytics-kpi-grid" aria-label="Leads por tipo">
        {(['contato', 'newsletter', 'integridade', 'sugestao_pauta'] as const).map((tipo) => (
          <KpiCard key={tipo} label={TIPO_LABELS[tipo]!} value={n(leads?.[tipo] ?? 0)} />
        ))}
      </section>

      <section className="analytics-block" aria-label="Leads ao longo do tempo">
        <header className="analytics-block__head">
          <h2 className="analytics-block__title">Volume diário ({n(totalLeads)} no período)</h2>
        </header>
        {conv.data ? (
          <LineChart buckets={buckets} previousBuckets={null} metric="visitors" />
        ) : (
          <p className="analytics-empty">Carregando...</p>
        )}
      </section>

      <section className="analytics-grid-2">
        <article className="analytics-block">
          <header className="analytics-block__head">
            <h2 className="analytics-block__title">Distribuição por persona</h2>
          </header>
          {personaData.length === 0 ? (
            <p className="analytics-empty">Sem leads de contato/newsletter no período.</p>
          ) : (
            <Donut data={personaData} centerLabel={n(totalLeads)} />
          )}
        </article>

        <article className="analytics-block">
          <header className="analytics-block__head">
            <h2 className="analytics-block__title">Eventos custom (Plausible)</h2>
          </header>
          <ul className="analytics-events-list">
            {(conv.data?.customEvents ?? []).map((e) => (
              <li key={e.name}>
                <code>{e.name}</code>
                <strong>{n(e.count)}</strong>
              </li>
            ))}
          </ul>
          <p className="analytics-block__note">
            <code>pdf_download</code> e <code>article_read</code> chegam quando a Plausible Stats
            API estiver online em produção (Fase 9).
          </p>
        </article>
      </section>
    </div>
  );
}
